import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Square, Activity, Wifi, Smartphone, Monitor, MessageCircle } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

type Platform = 'whatsapp' | 'signal';

interface TrackerData {
    rtt: number;
    avg: number;
    median: number;
    threshold: number;
    state: string;
    timestamp: number;
}

interface DeviceInfo {
    jid: string;
    state: string;
    rtt: number;
    avg: number;
}

interface ContactCardProps {
    jid: string;
    displayNumber: string;
    data: TrackerData[];
    devices: DeviceInfo[];
    deviceCount: number;
    presence: string | null;
    profilePic: string | null;
    onRemove: () => void;
    privacyMode?: boolean;
    platform?: Platform;
    showHeader?: boolean;
    selectedDevice?: string | null;
}

function getBestDeviceState(devices: DeviceInfo[]): string {
    if (devices.length === 0) return 'Unknown';
    const rank = (state: string) => {
        if (state.includes('Online')) return 0;
        if (state === 'Standby') return 1;
        if (state.includes('Calibrating')) return 2;
        if (state === 'OFFLINE') return 3;
        return 4;
    };
    return [...devices].sort((a, b) => rank(a.state) - rank(b.state))[0]?.state ?? 'Unknown';
}

export function ContactCard({
    jid,
    displayNumber,
    data,
    devices,
    deviceCount,
    presence,
    profilePic,
    onRemove,
    privacyMode = false,
    platform = 'whatsapp',
    showHeader = true,
    selectedDevice = null
}: ContactCardProps) {
    const { t } = useTranslation();
    const lastData = data[data.length - 1];

    let currentStatus: string;
    if (selectedDevice) {
        const selected = devices.find(d => d.jid === selectedDevice);
        currentStatus = selected?.state ?? getBestDeviceState(devices);
    } else {
        currentStatus = getBestDeviceState(devices);
    }

    const blurredNumber = privacyMode ? displayNumber.replace(/\d/g, '•') : displayNumber;

    return (
        <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
            {showHeader && (
                <div className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className={clsx(
                            "px-2 py-1 rounded text-xs font-medium flex items-center gap-1",
                            platform === 'whatsapp' ? "bg-emerald-500/10 text-emerald-400" : "bg-blue-500/10 text-blue-400"
                        )}>
                            <MessageCircle size={12} />
                            {platform === 'whatsapp' ? 'WhatsApp' : 'Signal'}
                        </span>
                        <h3 className="text-lg font-semibold text-white">{blurredNumber}</h3>
                    </div>
                    <button
                        onClick={onRemove}
                        className="px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg hover:bg-red-500/20 flex items-center gap-2 font-medium transition-colors text-sm"
                    >
                        <Square size={16} /> {t('dashboard.stop')}
                    </button>
                </div>
            )}

            <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* Status Card */}
                    <div className="bg-white/[0.03] border border-white/5 p-5 rounded-xl flex flex-col items-center text-center">
                        <div className="relative mb-4">
                            <div className="w-24 h-24 rounded-full overflow-hidden bg-white/5 ring-2 ring-white/5">
                                {profilePic ? (
                                    <img
                                        src={profilePic}
                                        alt="Profile"
                                        className={clsx(
                                            "w-full h-full object-cover transition-all duration-200",
                                            privacyMode && "blur-xl scale-110"
                                        )}
                                        style={privacyMode ? { filter: 'blur(16px) contrast(0.8)' } : {}}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-white/20 text-2xl font-bold">
                                        {blurredNumber[0]?.toUpperCase() ?? '?'}
                                    </div>
                                )}
                            </div>
                            <div className={clsx(
                                "absolute bottom-1 right-1 w-5 h-5 rounded-full border-2 border-slate-950",
                                currentStatus === 'OFFLINE' ? "bg-red-500" :
                                    currentStatus.includes('Online') ? "bg-emerald-500" :
                                        currentStatus === 'Standby' ? "bg-amber-500" : "bg-white/20"
                            )} />
                        </div>

                        <h4 className="text-lg font-bold text-white mb-1">{blurredNumber}</h4>

                        <div className="flex items-center gap-2 mb-4">
                            <span className={clsx(
                                "px-3 py-1 rounded-full text-xs font-semibold",
                                currentStatus === 'OFFLINE' ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                                    currentStatus.includes('Online') ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                                        currentStatus === 'Standby' ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                                            "bg-white/5 text-white/40 border border-white/10"
                            )}>
                                {currentStatus}
                            </span>
                        </div>

                        <div className="w-full pt-4 border-t border-white/5 space-y-2">
                            <div className="flex justify-between items-center text-sm text-white/50">
                                <span className="flex items-center gap-1"><Wifi size={14} /> {t('contactCard.officialStatus')}</span>
                                <span className="font-medium text-white/70">{presence || t('dashboard.unknown')}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-white/50">
                                <span className="flex items-center gap-1"><Smartphone size={14} /> Devices</span>
                                <span className="font-medium text-white/70">{deviceCount || 0}</span>
                            </div>
                        </div>

                        {/* Device List */}
                        {devices.length > 0 && (
                            <div className="w-full pt-4 border-t border-white/5 mt-4">
                                <h5 className="text-xs font-semibold text-white/30 uppercase mb-2">{t('contactCard.deviceStates')}</h5>
                                <div className="space-y-1">
                                    {devices.map((device, idx) => {
                                        const isPrimary = selectedDevice ? device.jid === selectedDevice : idx === 0;
                                        return (
                                            <div key={device.jid} className={clsx(
                                                "flex items-center justify-between text-sm py-1.5 px-2 rounded-lg",
                                                isPrimary ? "bg-cyan-500/5 ring-1 ring-cyan-500/20" : "bg-white/[0.02]"
                                            )}>
                                                <div className="flex items-center gap-2">
                                                    <Monitor size={13} className={isPrimary ? "text-cyan-400" : "text-white/20"} />
                                                    <span className={isPrimary ? "text-cyan-400 font-medium" : "text-white/50"}>
                                                        Device {idx + 1}
                                                        {isPrimary && <span className="ml-1 text-[10px] uppercase text-cyan-500/60">primary</span>}
                                                    </span>
                                                </div>
                                                <span className={clsx(
                                                    "px-2 py-0.5 rounded text-xs font-medium",
                                                    device.state === 'OFFLINE' ? "bg-red-500/10 text-red-400" :
                                                        device.state.includes('Online') ? "bg-emerald-500/10 text-emerald-400" :
                                                            device.state === 'Standby' ? "bg-amber-500/10 text-amber-400" :
                                                                "bg-white/5 text-white/40"
                                                )}>
                                                    {device.state}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Metrics & Chart */}
                    <div className="md:col-span-2 space-y-5">
                        {/* Metrics Grid */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white/[0.03] border border-white/5 p-4 rounded-xl">
                                <div className="text-xs text-white/30 mb-1 flex items-center gap-1"><Activity size={13} /> {t('contactCard.currentAvgRtt')}</div>
                                <div className="text-xl font-bold text-white">{lastData?.avg.toFixed(0) || '-'} <span className="text-sm text-white/30">ms</span></div>
                            </div>
                            <div className="bg-white/[0.03] border border-white/5 p-4 rounded-xl">
                                <div className="text-xs text-white/30 mb-1">{t('contactCard.median')}</div>
                                <div className="text-xl font-bold text-white">{lastData?.median.toFixed(0) || '-'} <span className="text-sm text-white/30">ms</span></div>
                            </div>
                            <div className="bg-white/[0.03] border border-white/5 p-4 rounded-xl">
                                <div className="text-xs text-white/30 mb-1">{t('contactCard.threshold')}</div>
                                <div className="text-xl font-bold text-cyan-400">{lastData?.threshold.toFixed(0) || '-'} <span className="text-sm text-cyan-400/50">ms</span></div>
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="bg-white/[0.03] border border-white/5 p-5 rounded-xl h-[280px]">
                            <h5 className="text-xs font-semibold text-white/30 mb-4">{t('contactCard.rttHistory')}</h5>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.04)" />
                                    <XAxis dataKey="timestamp" hide />
                                    <YAxis
                                        domain={['auto', 'auto']}
                                        stroke="rgba(255,255,255,0.1)"
                                        tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 11 }}
                                    />
                                    <Tooltip
                                        labelFormatter={(label: unknown) => {
                                            const ts = typeof label === 'number' ? label : Number(label);
                                            if (!Number.isFinite(ts)) return String(label ?? '');
                                            return new Date(ts).toLocaleTimeString();
                                        }}
                                        contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                                        labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                                    />
                                    <Line type="monotone" dataKey="avg" stroke="#34d399" strokeWidth={2} dot={false} name="Avg RTT" isAnimationActive={false} />
                                    <Line type="step" dataKey="threshold" stroke="#ef4444" strokeDasharray="5 5" dot={false} name="Threshold" isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
